import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

type SkipReason =
  | 'no_ticket_ref'
  | 'ticket_not_in_t3'
  | 'agent_not_in_t3'
  | 'already_imported'
  | 'zero_hours';

interface SkipSample {
  harvestId: number;
  notes: string | null;
}

interface ImportResult {
  scanned: number;
  imported: number;
  skipped: Record<SkipReason, number>;
  skippedSamples: Record<SkipReason, SkipSample[]>;
  failures: Array<{ harvestId: number; error: string }>;
}

const SKIP_LABELS: Record<SkipReason, string> = {
  no_ticket_ref: 'No #NNN in entry notes',
  ticket_not_in_t3: '#NNN found, but Zendesk ticket not in T3',
  agent_not_in_t3: 'Harvest user email not matched to a T3 agent',
  already_imported: 'Already imported on a previous run',
  zero_hours: 'Entry had zero hours',
};

type CsvBucket = 'block_hours' | 'non_billable_pm' | 'non_billable_internal' | 'chat_assist';

interface CsvPlanSummary {
  totalRows: number;
  rowsToImport: number;
  rowsAlreadyImported: number;
  rowsZeroHours: number;
  rowsMissingAgent: number;
  rowsInvalidDate: number;
  ticketedCount: number;
  refUnmatchedCount: number;
  noRefCount: number;
  bucketBreakdown: Record<CsvBucket, number>;
  agentsToCreate: Array<{ name: string; syntheticEmail: string }>;
  agentsExisting: string[];
  refUnmatchedSamples: Array<{ rowIndex: number; zendeskTicketId: number | null; notes: string }>;
  noRefSamples: Array<{ rowIndex: number; client: string; task: string; notes: string }>;
}

interface CsvCommitResult {
  imported: number;
  agentsCreated: number;
  ticketed: number;
  ticketless: number;
  consumptionsRecorded: number;
  failures: Array<{ rowIndex: number; error: string }>;
}

const BUCKET_LABELS: Record<CsvBucket, string> = {
  block_hours: 'Block hours (billable)',
  non_billable_pm: 'Non-billable PM / scoping',
  non_billable_internal: 'Non-billable internal (meetings, learning)',
  chat_assist: 'Chat assistance',
};

export function AdminHarvestImportPage(): React.ReactElement {
  useDocumentTitle('Harvest time entry import');
  const { agent } = useAuth();

  const [configured, setConfigured] = useState<boolean | null>(null);
  const [since, setSince] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // CSV importer state. Kept independent of the API importer above.
  const csvFileRef = useRef<HTMLInputElement | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvBusy, setCsvBusy] = useState<'idle' | 'previewing' | 'committing'>('idle');
  const [csvPlan, setCsvPlan] = useState<CsvPlanSummary | null>(null);
  const [csvCommit, setCsvCommit] = useState<CsvCommitResult | null>(null);
  const [csvError, setCsvError] = useState<string | null>(null);

  // Re-link state. The relink button is independent of the CSV upload —
  // it acts on whatever orphan time_entries already exist in the DB,
  // re-checking each against zendesk_ticket_map.
  const [relinkBusy, setRelinkBusy] = useState(false);
  const [relinkResult, setRelinkResult] = useState<{
    scanned: number;
    relinked: number;
    stillOrphanCount: number;
    stillOrphanZendeskIds: number[];
    failures: Array<{ timeEntryId: string; error: string }>;
  } | null>(null);
  const [relinkError, setRelinkError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ configured: boolean }>('/api/admin/harvest-import/status')
      .then((r) => setConfigured(r.configured))
      .catch(() => setConfigured(false));
  }, []);

  async function runImport() {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const sinceIso = since ? new Date(`${since}T00:00:00Z`).toISOString() : null;
      const r = await apiFetch<ImportResult>('/api/admin/harvest-import/time-entries', {
        method: 'POST',
        json: { since: sinceIso },
      });
      setResult(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Import failed.');
    } finally {
      setRunning(false);
    }
  }

  function clearCsvState() {
    setCsvPlan(null);
    setCsvCommit(null);
    setCsvError(null);
  }

  async function runCsvPreview() {
    if (!csvFile) return;
    setCsvBusy('previewing');
    clearCsvState();
    try {
      const fd = new FormData();
      fd.append('file', csvFile);
      const plan = await apiFetch<CsvPlanSummary>('/api/admin/harvest-import/csv/preview', {
        method: 'POST',
        body: fd,
      });
      setCsvPlan(plan);
    } catch (err) {
      setCsvError(err instanceof ApiError ? err.message : 'Preview failed.');
    } finally {
      setCsvBusy('idle');
    }
  }

  async function runRelink() {
    setRelinkBusy(true);
    setRelinkError(null);
    setRelinkResult(null);
    try {
      const r = await apiFetch<{
        scanned: number;
        relinked: number;
        stillOrphanCount: number;
        stillOrphanZendeskIds: number[];
        failures: Array<{ timeEntryId: string; error: string }>;
      }>('/api/admin/harvest-import/csv/relink-orphans', { method: 'POST' });
      setRelinkResult(r);
    } catch (err) {
      setRelinkError(err instanceof ApiError ? err.message : 'Re-link failed.');
    } finally {
      setRelinkBusy(false);
    }
  }

  async function runCsvCommit() {
    if (!csvFile) return;
    if (!confirm(
      'This will create time entries (and inactive agents for any names not in T3). ' +
      'Re-runs are safe — already-imported rows are skipped. Continue?',
    )) {
      return;
    }
    setCsvBusy('committing');
    setCsvError(null);
    setCsvCommit(null);
    try {
      const fd = new FormData();
      fd.append('file', csvFile);
      const r = await apiFetch<{ plan: CsvPlanSummary; result: CsvCommitResult }>(
        '/api/admin/harvest-import/csv/commit',
        { method: 'POST', body: fd },
      );
      setCsvPlan(r.plan);
      setCsvCommit(r.result);
    } catch (err) {
      setCsvError(err instanceof ApiError ? err.message : 'Commit failed.');
    } finally {
      setCsvBusy('idle');
    }
  }

  if (!agent) return <p>Loading…</p>;
  if (agent.role !== 'admin' && agent.role !== 'time_admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Admin
        </Link>
        <h1 className="mt-1 text-3xl font-bold text-[var(--color-header)]">
          Harvest time entry import
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-[var(--color-muted)]">
          Two paths: the live API path pulls from Harvest and matches users by email; the CSV
          path is for one-time historical backfill from a Harvest export. Both share the same{' '}
          <code>zendesk_ticket_map</code> for ticket resolution and are idempotent — re-runs
          skip rows already imported.
        </p>
      </div>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-[var(--color-header)]">API import</h2>

        {configured === false && (
          <Card className="border-amber-300 bg-amber-50/40">
            <p className="text-sm text-amber-900">
              Harvest is not configured. Set <code>HARVEST_API_KEY</code> and{' '}
              <code>HARVEST_ACCOUNT_ID</code> in your environment, then restart the backend.
            </p>
          </Card>
        )}

        {configured && (
          <Card>
            <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
              <FormField
                label="Updated since (optional)"
                name="since"
                type="date"
                value={since}
                onChange={(e) => setSince(e.target.value)}
                hint="Leave blank to import all of history. For the cutover-day delta, set to the last successful run."
              />
              <Button onClick={() => void runImport()} disabled={running}>
                {running ? 'Importing…' : 'Run import'}
              </Button>
            </div>
          </Card>
        )}

        {error && (
          <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
            <p className="text-sm text-[var(--color-danger)]">{error}</p>
          </Card>
        )}

        {result && (
          <>
            <Card>
              <h3 className="text-base font-semibold text-[var(--color-header)]">Result</h3>
              <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                <Stat label="Scanned" value={result.scanned} />
                <Stat label="Imported" value={result.imported} accent="emerald" />
                <Stat label="Failures" value={result.failures.length} accent={result.failures.length > 0 ? 'danger' : undefined} />
              </dl>
            </Card>

            <Card>
              <h3 className="text-base font-semibold text-[var(--color-header)]">Skipped, by reason</h3>
              <ul className="mt-3 space-y-3 text-sm">
                {(Object.keys(result.skipped) as SkipReason[]).map((reason) => {
                  const count = result.skipped[reason];
                  if (count === 0) return null;
                  const samples = result.skippedSamples[reason];
                  return (
                    <li key={reason} className="rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white p-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-[var(--color-header)]">
                          {SKIP_LABELS[reason]}
                        </span>
                        <span className="font-mono text-sm text-[var(--color-muted)]">{count}</span>
                      </div>
                      {samples.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-xs text-[var(--color-t3-blue)] hover:underline">
                            Show first {samples.length} sample{samples.length === 1 ? '' : 's'}
                          </summary>
                          <ul className="mt-2 space-y-1 text-xs">
                            {samples.map((s) => (
                              <li
                                key={s.harvestId}
                                className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1"
                              >
                                <span className="font-mono text-[var(--color-muted)]">#{s.harvestId}</span>{' '}
                                <span className="text-[var(--color-body)]">{s.notes ?? '(no notes)'}</span>
                              </li>
                            ))}
                          </ul>
                        </details>
                      )}
                    </li>
                  );
                })}
                {(Object.keys(result.skipped) as SkipReason[]).every((r) => result.skipped[r] === 0) && (
                  <li className="text-[var(--color-muted)]">Nothing skipped.</li>
                )}
              </ul>
            </Card>

            {result.failures.length > 0 && (
              <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
                <h3 className="text-base font-semibold text-[var(--color-danger)]">Failures</h3>
                <ul className="mt-3 space-y-1 text-xs">
                  {result.failures.map((f) => (
                    <li
                      key={f.harvestId}
                      className="rounded border border-[var(--color-danger)]/30 bg-white px-2 py-1"
                    >
                      <span className="font-mono">#{f.harvestId}</span> · {f.error}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </>
        )}
      </section>

      <section className="space-y-4">
        <h2 className="text-xl font-semibold text-[var(--color-header)]">CSV import (historical backfill)</h2>
        <p className="max-w-3xl text-sm text-[var(--color-muted)]">
          Upload a Harvest time-entry export. Ticketed rows (any with a{' '}
          <code>/tickets/NNN</code> URL or leading <code>#NNN:</code> in notes) land on the
          matching T3 ticket via <code>zendesk_ticket_map</code>. Non-ticket rows (T3 Internal,
          check-ins, meetings) get logged with <code>ticket_id=NULL</code> into the
          corresponding non-billable bucket — the original client name is preserved in the
          note prefix. Agent names not in T3 are created as inactive agents
          (<code>firstname.lastname@import.local</code>) so they can be activated later from
          the Agents admin page. Idempotent via row hash.
        </p>

        <Card>
          <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end">
            <div>
              <label className="block text-sm font-medium text-[var(--color-body)]">
                Harvest CSV export
              </label>
              <input
                ref={csvFileRef}
                type="file"
                accept=".csv,text/csv"
                className="mt-1 block w-full cursor-pointer text-sm text-[var(--color-muted)]
                           file:mr-3 file:cursor-pointer file:rounded-[var(--radius-button)]
                           file:border-0 file:bg-[var(--color-t3-blue)] file:px-4 file:py-2
                           file:text-sm file:font-semibold file:text-white
                           hover:file:brightness-95"
                onChange={(e) => {
                  setCsvFile(e.target.files?.[0] ?? null);
                  clearCsvState();
                }}
              />
              {csvFile && (
                <p className="mt-1 text-xs text-[var(--color-muted)]">
                  {csvFile.name} · {(csvFile.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>
            <Button
              variant="secondary"
              onClick={() => void runCsvPreview()}
              disabled={!csvFile || csvBusy !== 'idle'}
            >
              {csvBusy === 'previewing' ? 'Previewing…' : 'Preview (dry run)'}
            </Button>
            <Button
              onClick={() => void runCsvCommit()}
              disabled={!csvFile || csvBusy !== 'idle' || !csvPlan}
            >
              {csvBusy === 'committing' ? 'Committing…' : 'Commit import'}
            </Button>
          </div>
        </Card>

        {csvError && (
          <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
            <p className="text-sm text-[var(--color-danger)]">{csvError}</p>
          </Card>
        )}

        <Card>
          <h3 className="text-base font-semibold text-[var(--color-header)]">
            Re-link orphan time entries
          </h3>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            If you imported the CSV before all Zendesk tickets were imported, some time entries
            ended up with no ticket attached (they carry a <code>[Zendesk #N not in T3]</code>{' '}
            note prefix). Run this after the Zendesk import is up to date to re-attach those
            entries to the tickets that now exist. Safe to re-run.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <Button variant="secondary" onClick={() => void runRelink()} disabled={relinkBusy}>
              {relinkBusy ? 'Re-linking…' : 'Re-link orphans'}
            </Button>
            {relinkError && (
              <span className="text-sm text-[var(--color-danger)]">{relinkError}</span>
            )}
          </div>
          {relinkResult && (
            <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
              <Stat label="Scanned" value={relinkResult.scanned} />
              <Stat label="Re-linked" value={relinkResult.relinked} accent="emerald" />
              <Stat
                label="Still orphan"
                value={relinkResult.stillOrphanCount}
                accent={relinkResult.stillOrphanCount > 0 ? 'danger' : undefined}
              />
              <Stat
                label="Failures"
                value={relinkResult.failures.length}
                accent={relinkResult.failures.length > 0 ? 'danger' : undefined}
              />
            </dl>
          )}
          {relinkResult && relinkResult.stillOrphanZendeskIds.length > 0 && (
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer text-[var(--color-muted)] hover:underline">
                Zendesk ticket ids still unmatched (first {relinkResult.stillOrphanZendeskIds.length})
              </summary>
              <p className="mt-2 font-mono text-xs text-[var(--color-muted)]">
                {relinkResult.stillOrphanZendeskIds.join(', ')}
              </p>
            </details>
          )}
        </Card>

        {csvPlan && (
          <>
            <Card>
              <h3 className="text-base font-semibold text-[var(--color-header)]">
                {csvCommit ? 'Commit result' : 'Dry-run summary'}
              </h3>
              <dl className="mt-3 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                <Stat label="Rows in CSV" value={csvPlan.totalRows} />
                <Stat label="To import" value={csvPlan.rowsToImport} accent="emerald" />
                <Stat label="Already imported" value={csvPlan.rowsAlreadyImported} />
                <Stat label="Zero hours / skipped" value={csvPlan.rowsZeroHours + csvPlan.rowsMissingAgent + csvPlan.rowsInvalidDate} />
                <Stat label="Will land on a T3 ticket" value={csvPlan.ticketedCount} accent="emerald" />
                <Stat label="Ticket ref not in T3" value={csvPlan.refUnmatchedCount} accent={csvPlan.refUnmatchedCount > 0 ? 'danger' : undefined} />
                <Stat label="No ticket ref" value={csvPlan.noRefCount} />
                <Stat label="Agents to create" value={csvPlan.agentsToCreate.length} />
              </dl>

              {csvCommit && (
                <dl className="mt-4 grid gap-x-6 gap-y-2 border-t border-[var(--color-border)] pt-3 text-sm sm:grid-cols-4">
                  <Stat label="Imported" value={csvCommit.imported} accent="emerald" />
                  <Stat label="Agents created" value={csvCommit.agentsCreated} accent="emerald" />
                  <Stat label="On tickets" value={csvCommit.ticketed} />
                  <Stat label="Ticket-less" value={csvCommit.ticketless} />
                  <Stat label="Block-hour consumption rows" value={csvCommit.consumptionsRecorded} accent="emerald" />
                  <Stat label="Failures" value={csvCommit.failures.length} accent={csvCommit.failures.length > 0 ? 'danger' : undefined} />
                </dl>
              )}
            </Card>

            <Card>
              <h3 className="text-base font-semibold text-[var(--color-header)]">Bucket breakdown</h3>
              <ul className="mt-3 space-y-1 text-sm">
                {(Object.keys(csvPlan.bucketBreakdown) as CsvBucket[]).map((b) => (
                  <li key={b} className="flex justify-between rounded border border-[var(--color-border)] bg-white px-3 py-1">
                    <span className="text-[var(--color-body)]">{BUCKET_LABELS[b]}</span>
                    <span className="font-mono text-[var(--color-muted)]">{csvPlan.bucketBreakdown[b]}</span>
                  </li>
                ))}
              </ul>
            </Card>

            {csvPlan.agentsToCreate.length > 0 && (
              <Card>
                <h3 className="text-base font-semibold text-[var(--color-header)]">
                  Agents to be created (inactive)
                </h3>
                <p className="mt-1 text-xs text-[var(--color-muted)]">
                  Created with <code>role=agent</code>, <code>is_active=false</code>, no
                  password / TOTP. Activate from the Agents admin page when you know their
                  real email.
                </p>
                <ul className="mt-3 space-y-1 text-sm">
                  {csvPlan.agentsToCreate.map((a) => (
                    <li key={a.name} className="flex justify-between rounded border border-[var(--color-border)] bg-white px-3 py-1">
                      <span className="text-[var(--color-body)]">{a.name}</span>
                      <span className="font-mono text-xs text-[var(--color-muted)]">{a.syntheticEmail}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {csvPlan.refUnmatchedSamples.length > 0 && (
              <Card className="border-amber-300 bg-amber-50/40">
                <h3 className="text-base font-semibold text-[var(--color-header)]">
                  Ticket reference not in T3 (first {csvPlan.refUnmatchedSamples.length})
                </h3>
                <p className="mt-1 text-xs text-[var(--color-muted)]">
                  These rows reference a Zendesk ticket id but that ticket isn't in
                  <code> zendesk_ticket_map</code> — likely never imported. They'll still be
                  logged (<code>ticket_id=NULL</code>) with the original reference in the
                  note. Import the missing Zendesk tickets first if you want them attached.
                </p>
                <ul className="mt-3 space-y-1 text-xs">
                  {csvPlan.refUnmatchedSamples.map((s) => (
                    <li
                      key={s.rowIndex}
                      className="rounded border border-amber-200 bg-white px-2 py-1"
                    >
                      <span className="font-mono text-[var(--color-muted)]">
                        row {s.rowIndex} · Zendesk #{s.zendeskTicketId}
                      </span>{' '}
                      <span className="text-[var(--color-body)]">{s.notes || '(no notes)'}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {csvCommit && csvCommit.failures.length > 0 && (
              <Card className="border-[var(--color-danger)]/30 bg-red-50/40">
                <h3 className="text-base font-semibold text-[var(--color-danger)]">Failures</h3>
                <ul className="mt-3 space-y-1 text-xs">
                  {csvCommit.failures.map((f) => (
                    <li
                      key={f.rowIndex}
                      className="rounded border border-[var(--color-danger)]/30 bg-white px-2 py-1"
                    >
                      <span className="font-mono">row {f.rowIndex}</span> · {f.error}
                    </li>
                  ))}
                </ul>
              </Card>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: number;
  accent?: 'emerald' | 'danger';
}): React.ReactElement {
  const valueClass =
    accent === 'emerald'
      ? 'text-emerald-700'
      : accent === 'danger'
        ? 'text-[var(--color-danger)]'
        : 'text-[var(--color-header)]';
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-[var(--color-muted)]">{label}</dt>
      <dd className={`text-2xl font-bold ${valueClass}`}>{value}</dd>
    </div>
  );
}
